import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, day, money } from '../api'
import { ClaimTable } from './Claims'

/**
 * The claims desk's work: every open claim, whoever holds the policy, and the
 * rent bonds in force — a lease that ends early is recorded on the bond.
 * Deciding a claim happens on the claim itself, where its evidence and
 * history are.
 */
export default function Queue() {
  const { t } = useTranslation()
  const open = useQuery({
    queryKey: ['queue'],
    queryFn: () => api.claims({ all: true, status: 'open' }),
    refetchInterval: 15_000,
  })
  const recent = useQuery({ queryKey: ['queue', 'all'], queryFn: () => api.claims({ all: true }) })
  const closed = (recent.data?.data ?? []).filter(c => c.status !== 'open').slice(0, 25)
  const policies = useQuery({ queryKey: ['policies', 'all'], queryFn: () => api.policies({ all: true }), refetchInterval: 30_000 })
  const bonds = (policies.data?.data ?? []).filter(p => p.kind === 'bond' && (p.status === 'active' || p.status === 'cancelled'))

  return (
    <div className="page page-mid stack">
      <div className="panel">
        <div className="panel-head"><span className="grow">{t('openClaims')}</span></div>
        <div className="panel-body flush">
          {open.isLoading && <div className="panel-body"><div className="skeleton" style={{ height: 60 }} /></div>}
          {!open.isLoading && (open.data?.data.length ?? 0) === 0 && <p className="empty">{t('noOpenClaims')}</p>}
          {(open.data?.data.length ?? 0) > 0 && <ClaimTable claims={open.data!.data} showHolder />}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><span className="grow">{t('bondsDesk')}</span></div>
        <div className="panel-body flush">
          {bonds.length === 0 && <p className="empty">{t('noBonds')}</p>}
          {bonds.length > 0 && (
            <div className="scroll-x">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('bondWord')}</th><th>{t('renter')}</th><th>{t('landlord')}</th>
                    <th className="num">{t('inEscrow')}</th><th>{t('releaseOn')}</th><th>{t('status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {bonds.map(p => (
                    <tr key={p.id}>
                      <td><Link to={`/policies/${p.id}`}>{p.product_name || p.id}</Link></td>
                      <td>{p.holder}</td>
                      <td>{p.beneficiary}</td>
                      <td className="num">{money(p.remaining)}</td>
                      <td className="nowrap">{day(p.release_at)}</td>
                      <td><span className={`state ${p.status}`}>{t(p.status)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {closed.length > 0 && (
        <div className="panel">
          <div className="panel-head"><span className="grow">{t('history')}</span></div>
          <div className="panel-body flush"><ClaimTable claims={closed} showHolder /></div>
        </div>
      )}
    </div>
  )
}
